import { format, isToday, isYesterday, differenceInCalendarDays } from 'date-fns'
import { fr } from 'date-fns/locale'

// ─── Date ─────────────────────────────────────────────────────────────────────

/** Numéro du jour dans l'année (1-365/366) — utilisé pour le verset du jour */
export function getDayOfYear(): number {
  const now = new Date()
  const start = new Date(now.getFullYear(), 0, 0)
  return Math.floor((Number(now) - Number(start)) / 86_400_000)
}

/** "lundi 3 mars 2025" */
export function formatDateFR(date: Date): string {
  return format(date, "EEEE d MMMM yyyy", { locale: fr })
}

/** "07h30" */
export function formatTimeFR(date: Date): string {
  return format(date, "HH'h'mm", { locale: fr })
}

/** "03/03/2025" */
export function formatShortDate(date: Date): string {
  return format(date, 'dd/MM/yyyy')
}

/** "lundi 9 mars 2026 à 17h34" */
export function formatDateTimeFR(date: Date): string {
  return format(date, "EEEE d MMMM yyyy 'à' HH'h'mm", { locale: fr })
}

/** "9 mars à 17h34" */
export function formatShortDateTime(date: Date): string {
  return format(date, "d MMMM 'à' HH'h'mm", { locale: fr })
}

/** ISO date "2025-03-03" pour Supabase */
export function toISODate(date: Date = new Date()): string {
  return format(date, 'yyyy-MM-dd')
}

/** Parse une date ISO Supabase en objet Date */
export function fromISODate(iso: string): Date {
  return new Date(`${iso}T00:00:00`)
}

/** Libellé relatif court : "Aujourd'hui", "Hier", "Il y a 3 j" */
export function relativeDay(date: Date): string {
  if (isToday(date)) return "Aujourd'hui"
  if (isYesterday(date)) return 'Hier'
  const diff = differenceInCalendarDays(new Date(), date)
  if (diff < 30) return `Il y a ${diff} j`
  return formatShortDate(date)
}

// ─── Streak logic ─────────────────────────────────────────────────────────────

/** Calcule le prochain état du streak selon last_active_date */
export function computeStreak(
  currentStreak: number,
  lastActiveDate: string | null,
): { streak_count: number; last_active_date: string } {
  const today = toISODate()

  if (!lastActiveDate) {
    return { streak_count: 1, last_active_date: today }
  }

  if (lastActiveDate === today) {
    // Déjà actif aujourd'hui → pas de changement
    return { streak_count: currentStreak, last_active_date: today }
  }

  const last = fromISODate(lastActiveDate)
  const diff = differenceInCalendarDays(new Date(), last)

  if (diff === 1) {
    // Hier → on continue la série
    return { streak_count: currentStreak + 1, last_active_date: today }
  }

  // Rupture → on repart à 1
  return { streak_count: 1, last_active_date: today }
}

// ─── Texte ────────────────────────────────────────────────────────────────────

/** Extrait le titre depuis la première ligne d'un contenu texte */
export function extractTitleFromContent(content: string): string {
  if (!content) return 'Sans titre'
  const firstLine = content.split('\n')[0].trim()
  // Supprime les # markdown éventuels
  return firstLine.replace(/^#+\s*/, '').slice(0, 80) || 'Sans titre'
}

/** Tronque proprement un texte à maxLength caractères */
export function truncate(text: string, maxLength = 120): string {
  if (!text || text.length <= maxLength) return text
  return `${text.slice(0, maxLength).trimEnd()}…`
}

/** Capitalise la première lettre */
export function capitalize(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s
}

// ─── Classes Tailwind conditionnelles (mini-clsx) ────────────────────────────

export function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}
